import { useMemo, useState, type ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAuth,
  useCreateWebDataExportDownload,
  useRequestWebDataExport,
  useResendEmailVerification,
  useTenantBootstrap,
  useTheme,
  useUpdateWebEmailPreferences,
  useUpdateWebPeopleDiscoverySettings,
  useUpdateWebProfileVisibility,
  useWebDataExportStatus,
  useWebEmailPreferences,
  useWebPeopleDiscoverySettings,
  useWebProfile,
  THEME_ACCENT_OPTIONS,
  THEME_MODE_OPTIONS,
  type ThemeAccent,
  type ThemeMode,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import {
  AlertCircle,
  ArrowRight,
  ArrowRightLeft,
  Ban,
  CheckCircle2,
  CircleUserRound,
  Download,
  Edit2,
  ExternalLink,
  Globe2,
  HeartHandshake,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  Monitor,
  Moon,
  Palette,
  Phone,
  Settings,
  Shield,
  Sun,
  Trash2,
  Users2,
} from 'lucide-react';
import { AppPageFrame } from '../components/AppPageFrame';
import {
  cn,
  getErrorMessage,
  type WebEmailPreferences,
  type WebPeopleDiscoverySettings,
  type WebUserProfile,
} from '@tark/shared/lib';
import { isBuzzWebAdsEnabled, showGoogleAdPrivacyOptions } from '../lib/googleAdPrivacy';

type VisibilityMode = 'private' | 'squads' | 'public';

const VISIBILITY_OPTIONS: Array<{
  value: VisibilityMode;
  label: string;
  icon: ElementType;
}> = [
  { value: 'private', label: 'Private', icon: LockKeyhole },
  { value: 'squads', label: 'Squads only', icon: Users2 },
  { value: 'public', label: 'Public', icon: Globe2 },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, isLoggingOut, refetch } = useAuth();
  const profileQuery = useWebProfile(Boolean(user));
  const emailPreferencesQuery = useWebEmailPreferences(Boolean(user));
  const discoverySettingsQuery = useWebPeopleDiscoverySettings(Boolean(user));
  const resendEmailVerification = useResendEmailVerification();
  const updateVisibility = useUpdateWebProfileVisibility();
  const requestDataExport = useRequestWebDataExport();
  const dataExportStatusQuery = useWebDataExportStatus(Boolean(user));
  const createDataExportDownload = useCreateWebDataExportDownload();
  const updateEmailPreferences = useUpdateWebEmailPreferences();
  const updateDiscoverySettings = useUpdateWebPeopleDiscoverySettings();
  const { mode, setMode, accent, setAccent, resolvedTheme } = useTheme();
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [profileNotice, setProfileNotice] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const {
    memberships,
    activeTenant,
    selectTenant,
    isLoading: isTenantLoading,
    isSwitchingTenant,
  } = useTenantBootstrap(Boolean(user));

  const profile = profileQuery.data;
  const canManageTenantAccess = ['OWNER', 'ADMIN', 'COACH'].includes(activeTenant?.roleCode || '');
  const activeContextName = activeTenant?.tenant?.displayName || 'Community';
  const activeContextMeta = activeTenant?.roleCode
    ? `${activeTenant.roleCode} - ${activeTenant.tenant?.workTabLabel || 'Work'}`
    : 'Squads, play, PROs, and Buzz';
  const displayName = getDisplayName(profile, user?.displayName);
  const accountEmail = profile?.email?.trim() || user?.email?.trim() || null;
  const accountPhone = profile?.phoneNumber?.trim() || user?.phone?.trim() || null;
  const emailVerified = profile?.emailVerified ?? user?.emailVerified ?? false;
  const phoneVerified = profile?.mobileVerified ?? user?.mobileVerified ?? false;
  const shouldShowEmailVerification = Boolean(accountEmail && !emailVerified);
  const visibility = normalizeVisibility(profile?.visibility);
  const preferences = emailPreferencesQuery.data;
  const discoverySettings = discoverySettingsQuery.data;

  const profileFacts = useMemo(() => buildProfileFacts(profile), [profile]);

  const handleLogout = async () => {
    await logout();
  };

  const handleResendEmailVerification = async () => {
    setEmailNotice(null);
    setEmailError(null);

    try {
      const result = await resendEmailVerification.mutateAsync();
      const target = result.emailSentTo || result.email || accountEmail;
      setEmailNotice(
        result.status === 'already_verified'
          ? 'Email already verified.'
          : target
            ? `Verification sent to ${target}.`
            : result.message || 'Verification email sent.'
      );
      await Promise.all([refetch(), profileQuery.refetch()]);
    } catch (error) {
      setEmailError(resolveProfileErrorMessage(error));
    }
  };

  const handleVisibilityChange = async (nextVisibility: VisibilityMode) => {
    if (visibility === nextVisibility || updateVisibility.isPending) {
      return;
    }

    setProfileNotice(null);
    setProfileError(null);
    try {
      await updateVisibility.mutateAsync(nextVisibility);
      setProfileNotice('Visibility updated.');
    } catch (error) {
      setProfileError(resolveProfileErrorMessage(error));
    }
  };

  const handleDataExportAction = async () => {
    setProfileNotice(null);
    setProfileError(null);

    try {
      if (dataExportStatusQuery.isError && !dataExportStatusQuery.data) {
        const refreshed = await dataExportStatusQuery.refetch();
        if (refreshed.error) {
          throw refreshed.error;
        }
        return;
      }

      const current = dataExportStatusQuery.data;
      if (current?.status === 'READY' && current.canDownload && current.requestId) {
        const download = await createDataExportDownload.mutateAsync(current.requestId);
        window.location.assign(download.url);
        setProfileNotice('Download started.');
        return;
      }

      const result = await requestDataExport.mutateAsync();
      setProfileNotice(result.message || 'Your export is being prepared.');
    } catch (error) {
      setProfileError(resolveProfileErrorMessage(error));
    }
  };

  const handleEmailPreferenceToggle = async (key: keyof WebEmailPreferences) => {
    const current = preferences ?? {};
    setProfileNotice(null);
    setProfileError(null);

    try {
      await updateEmailPreferences.mutateAsync({
        ...current,
        [key]: !current[key],
      });
      setProfileNotice('Email preferences updated.');
    } catch (error) {
      setProfileError(resolveProfileErrorMessage(error));
    }
  };

  const handleDiscoveryToggle = async (
    key: 'discoverableByEmail' | 'discoverableByPhone'
  ) => {
    if (!discoverySettings || updateDiscoverySettings.isPending) {
      return;
    }

    setProfileNotice(null);
    setProfileError(null);
    try {
      await updateDiscoverySettings.mutateAsync({
        discoverableByEmail: discoverySettings.discoverableByEmail,
        discoverableByPhone: discoverySettings.discoverableByPhone,
        [key]: !discoverySettings[key],
      });
      setProfileNotice('Discovery settings updated.');
    } catch (error) {
      setProfileError(resolveProfileErrorMessage(error));
    }
  };

  const handleTenantSelect = async (tenantId: number) => {
    await selectTenant({ tenantId });
  };

  const handleCommunitySelect = async () => {
    await selectTenant({ communityMode: true });
  };

  const handleAdPrivacyChoices = async () => {
    setProfileNotice(null);
    setProfileError(null);

    const result = await showGoogleAdPrivacyOptions();
    if (result === 'shown') {
      return;
    }

    if (result === 'preview') {
      setProfileNotice('Ad privacy choices will be available when live ads are enabled on the approved public site.');
      return;
    }

    if (result === 'not-available') {
      setProfileNotice('Google has no additional ad privacy choices to show for this browser or region.');
      return;
    }

    setProfileError('Ad privacy choices could not be opened. Please try again later.');
  };

  if (profileQuery.isLoading && !profile) {
    return (
      <AppPageFrame className="flex min-h-[420px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </AppPageFrame>
    );
  }

  return (
    <AppPageFrame className="space-y-6">
      <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-text-muted">Me</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-text">Profile</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" leftIcon={<Edit2 className="h-4 w-4" />} onClick={() => navigate('/profile/edit')}>
            Edit Profile
          </Button>
          <Button variant="outline" leftIcon={<HeartHandshake className="h-4 w-4" />} onClick={() => navigate('/family')}>
            Family
          </Button>
        </div>
      </div>

      {profileQuery.isError ? (
        <InlineNotice
          tone="error"
          title="Profile refresh failed"
          message="Some account details may be stale. Try refreshing after a moment."
        />
      ) : null}

      {profileNotice ? <InlineNotice tone="success" title={profileNotice} /> : null}
      {profileError ? <InlineNotice tone="error" title={profileError} /> : null}

      {shouldShowEmailVerification ? (
        <EmailVerificationBanner
          email={accountEmail ?? ''}
          notice={emailNotice}
          error={emailError}
          isSending={resendEmailVerification.isPending}
          onResend={handleResendEmailVerification}
        />
      ) : null}

      <Card variant="glass">
        <CardContent className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl bg-primary/10 text-primary">
              {profile?.profilePicture || user?.avatarUrl ? (
                <img
                  src={profile?.profilePicture || user?.avatarUrl || ''}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <CircleUserRound className="h-10 w-10" />
              )}
            </div>
            <div>
              <p className="text-2xl font-semibold text-text">{displayName}</p>
              <p className="mt-1 text-sm text-text-muted">{accountPhone || 'No phone on file'}</p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">
                <ArrowRightLeft className="h-3.5 w-3.5 text-primary" />
                {activeContextName}
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <MiniStatus label="Email" ready={emailVerified} />
            <MiniStatus label="Phone" ready={phoneVerified} />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <Card variant="default">
            <CardHeader>
              <CardTitle>Active workspace</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">Current context</p>
                <p className="mt-2 text-lg font-semibold text-text">{activeContextName}</p>
                <p className="mt-1 text-sm text-text-muted">{activeContextMeta}</p>
              </div>

              {isTenantLoading ? (
                <p className="text-sm text-text-muted">Loading workspaces...</p>
              ) : (
                <div className="space-y-3">
                  {memberships.length > 0 ? (
                    <WorkspaceCard
                      title="Community"
                      subtitle="Casual squads, play history, PROs, and Buzz"
                      active={!activeTenant}
                      loading={isSwitchingTenant && Boolean(activeTenant)}
                      onClick={handleCommunitySelect}
                      disabled={!activeTenant}
                    />
                  ) : null}

                  {memberships.map((membership) => {
                    const tenant = membership.tenant;
                    if (!tenant?.id) {
                      return null;
                    }
                    const isActive = activeTenant?.tenant?.id === tenant.id;
                    return (
                      <WorkspaceCard
                        key={membership.membershipId}
                        title={tenant.displayName}
                        subtitle={[membership.roleCode, tenant.workTabLabel, tenant.directoryTabLabel].filter(Boolean).join(' - ')}
                        active={isActive}
                        loading={isSwitchingTenant && !isActive}
                        onClick={() => handleTenantSelect(tenant.id)}
                        disabled={isActive}
                      />
                    );
                  })}

                  {memberships.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/30 p-5">
                      <p className="font-semibold text-text">No organizations yet</p>
                      <p className="mt-2 text-sm text-text-muted">
                        Join codes and tenant invites will appear here after you connect to a coach or organization.
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>

          <Card variant="default">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Playing profile</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/profile/edit')}>
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {profileFacts.map((fact) => (
                  <ProfileFact key={fact.label} label={fact.label} value={fact.value} />
                ))}
              </div>
            </CardContent>
          </Card>

          <VisibilityCard
            visibility={visibility}
            isSaving={updateVisibility.isPending}
            onChange={handleVisibilityChange}
          />

          <PeopleDiscoveryCard
            settings={discoverySettings}
            isLoading={discoverySettingsQuery.isLoading}
            isSaving={updateDiscoverySettings.isPending}
            onToggle={handleDiscoveryToggle}
          />
        </div>

        <div className="space-y-4">
          <Card variant="default">
            <CardHeader>
              <CardTitle>Quick access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <QuickAction
                icon={Shield}
                title="Join Organization"
                description="Redeem a coach or academy invite."
                onClick={() => navigate('/join-organization')}
              />
              <QuickAction
                icon={HeartHandshake}
                title="Family"
                description="Manage guardian and athlete relationships."
                onClick={() => navigate('/family')}
              />
              {canManageTenantAccess ? (
                <QuickAction
                  icon={Settings}
                  title="Organization Access"
                  description="Invites, join codes, and members."
                  onClick={() => navigate('/tenant-access')}
                />
              ) : null}
            </CardContent>
          </Card>

          <Card variant="default">
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ContactStatusRow
                icon={Mail}
                label="Email"
                value={accountEmail || 'No email on file'}
                verified={emailVerified}
                missing={!accountEmail}
                actionLabel={accountEmail && !emailVerified ? 'Resend' : undefined}
                actionLoading={resendEmailVerification.isPending}
                onAction={accountEmail && !emailVerified ? handleResendEmailVerification : undefined}
              />
              <ContactStatusRow
                icon={Phone}
                label="Phone"
                value={accountPhone || 'No phone on file'}
                verified={phoneVerified}
                missing={!accountPhone}
                actionLabel={accountPhone && phoneVerified && emailVerified ? 'Change' : undefined}
                onAction={accountPhone && phoneVerified && emailVerified ? () => navigate('/profile/change-phone') : undefined}
              />
            </CardContent>
          </Card>

          <AppearanceCard
            mode={mode}
            accent={accent}
            resolvedTheme={resolvedTheme}
            onModeChange={setMode}
            onAccentChange={setAccent}
          />

          <Card variant="default">
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <PreferenceToggle
                label="Product updates"
                checked={Boolean(preferences?.marketingEmails)}
                loading={updateEmailPreferences.isPending}
                onClick={() => handleEmailPreferenceToggle('marketingEmails')}
              />
              <PreferenceToggle
                label="Match notifications"
                checked={preferences?.matchNotifications ?? true}
                loading={updateEmailPreferences.isPending}
                onClick={() => handleEmailPreferenceToggle('matchNotifications')}
              />
              <PreferenceToggle
                label="Coach reminders"
                checked={preferences?.messageNotifications ?? true}
                loading={updateEmailPreferences.isPending}
                onClick={() => handleEmailPreferenceToggle('messageNotifications')}
              />
              <PreferenceToggle
                label="Weekly digest"
                checked={preferences?.weeklyDigest ?? true}
                loading={updateEmailPreferences.isPending}
                onClick={() => handleEmailPreferenceToggle('weeklyDigest')}
              />
            </CardContent>
          </Card>

          <Card variant="default">
            <CardHeader>
              <CardTitle>Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ActionRow icon={ExternalLink} label="Privacy Policy" onClick={() => navigate('/privacy')} />
              <ActionRow icon={ExternalLink} label="Terms of Service" onClick={() => navigate('/terms')} />
              <ActionRow
                icon={Mail}
                label="Contact support"
                onClick={() => window.location.assign('mailto:support@playtark.com?subject=TARK%20support')}
              />
              {isBuzzWebAdsEnabled() ? (
                <ActionRow icon={Shield} label="Ad privacy choices" onClick={handleAdPrivacyChoices} />
              ) : null}
              <ActionRow
                icon={Download}
                label="Download my data"
                actionLabel={dataExportActionLabel(
                  dataExportStatusQuery.data?.status,
                  dataExportStatusQuery.isLoading,
                  dataExportStatusQuery.isError,
                  requestDataExport.isPending || createDataExportDownload.isPending
                )}
                disabled={
                  requestDataExport.isPending
                  || createDataExportDownload.isPending
                  || dataExportStatusQuery.isLoading
                  || dataExportStatusQuery.data?.status === 'PENDING'
                  || dataExportStatusQuery.data?.status === 'PROCESSING'
                }
                onClick={handleDataExportAction}
              />
              <ActionRow icon={Ban} label="Blocked users" actionLabel="Soon" disabled />
              <ActionRow icon={Trash2} label="Delete account" actionLabel="Soon" danger disabled />
            </CardContent>
          </Card>

          <Card variant="default">
            <CardHeader>
              <CardTitle>Session</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full justify-center border-red-500/30 text-red-500 hover:border-red-500/50 hover:bg-red-500/10"
                leftIcon={<LogOut className="h-4 w-4" />}
                onClick={handleLogout}
                isLoading={isLoggingOut}
              >
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppPageFrame>
  );
}

function AppearanceCard({
  mode,
  accent,
  resolvedTheme,
  onModeChange,
  onAccentChange,
}: {
  mode: ThemeMode;
  accent: ThemeAccent;
  resolvedTheme: 'light' | 'dark';
  onModeChange: (mode: ThemeMode) => void;
  onAccentChange: (accent: ThemeAccent) => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Appearance</CardTitle>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated/40 px-3 py-1 text-xs font-semibold capitalize text-text-muted">
            <Palette className="h-3.5 w-3.5 text-primary" />
            {resolvedTheme}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Mode</p>
          <div className="grid grid-cols-3 gap-1 rounded-2xl border border-border bg-surface-elevated/30 p-1">
            {THEME_MODE_OPTIONS.map((option) => {
              const Icon = getModeIcon(option.value);
              const selected = mode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onModeChange(option.value)}
                  className={cn(
                    'flex min-h-10 items-center justify-center gap-2 rounded-xl px-2 text-sm font-semibold transition-all',
                    selected
                      ? 'bg-surface text-primary shadow-[var(--shadow-xs)] ring-1 ring-primary/25'
                      : 'text-text-muted hover:bg-surface/70 hover:text-text'
                  )}
                  aria-pressed={selected}
                >
                  <Icon className="h-4 w-4" />
                  <span>{option.label}</span>
                  {selected ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Accent</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {THEME_ACCENT_OPTIONS.map((option) => {
              const selected = accent === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onAccentChange(option.value)}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-2xl border px-3 py-3 text-left transition-all',
                    selected
                      ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/15'
                      : 'border-border bg-surface-elevated/20 text-text hover:border-primary/40 hover:bg-surface-elevated/60'
                  )}
                  aria-pressed={selected}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-5 w-5 shrink-0 rounded-full border border-black/10 shadow-sm"
                      style={{
                        background: `linear-gradient(135deg, ${option.primary}, ${option.primaryLight})`,
                      }}
                      aria-hidden="true"
                    />
                    <span className="truncate text-sm font-semibold">{option.label}</span>
                  </span>
                  {selected ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-text">Preview</span>
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold [color:var(--color-on-primary)]">
              Selected
            </span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-primary/15">
            <div className="h-full w-2/3 rounded-full bg-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getModeIcon(mode: ThemeMode) {
  if (mode === 'dark') {
    return Moon;
  }

  if (mode === 'system') {
    return Monitor;
  }

  return Sun;
}

function EmailVerificationBanner({
  email,
  notice,
  error,
  isSending,
  onResend,
}: {
  email: string;
  notice: string | null;
  error: string | null;
  isSending: boolean;
  onResend: () => void;
}) {
  return (
    <div className="rounded-3xl border border-amber-200 bg-amber-50/80 p-4 text-amber-900 shadow-[var(--shadow-card)]">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-amber-100 p-3">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold">Verify your email</p>
            <p className="mt-1 text-sm text-amber-800">Required before submitting PRO requests. We will send the link to {email}.</p>
            {notice ? <p className="mt-2 text-sm font-medium text-primary">{notice}</p> : null}
            {error ? <p className="mt-2 text-sm font-medium text-red-600">{error}</p> : null}
          </div>
        </div>
        <Button
          variant="outline"
          className="shrink-0 border-amber-300 bg-white/70 text-amber-900 hover:border-primary/40 hover:text-primary"
          isLoading={isSending}
          onClick={onResend}
        >
          Resend email
        </Button>
      </div>
    </div>
  );
}

function VisibilityCard({
  visibility,
  isSaving,
  onChange,
}: {
  visibility: VisibilityMode;
  isSaving: boolean;
  onChange: (visibility: VisibilityMode) => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Profile visibility</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-3">
          {VISIBILITY_OPTIONS.map((option) => {
            const Icon = option.icon;
            const selected = visibility === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={isSaving}
                onClick={() => onChange(option.value)}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all',
                  selected
                    ? 'border-primary bg-primary/10 font-semibold text-primary ring-2 ring-primary/20'
                    : 'border-border bg-surface-elevated/25 text-text hover:border-primary/40 hover:bg-surface-elevated'
                )}
                aria-pressed={selected}
              >
                <span className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {option.label}
                </span>
                {selected ? <CheckCircle2 className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PeopleDiscoveryCard({
  settings,
  isLoading,
  isSaving,
  onToggle,
}: {
  settings?: WebPeopleDiscoverySettings;
  isLoading: boolean;
  isSaving: boolean;
  onToggle: (key: 'discoverableByEmail' | 'discoverableByPhone') => void;
}) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle>Find me on TARK</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-text-muted">
          Let verified TARK members find your profile using an exact verified detail. Your email and phone are never shown.
        </p>
        <PreferenceToggle
          label={settings?.emailVerified ? 'Find me by verified email' : 'Verify email to enable discovery'}
          checked={Boolean(settings?.discoverableByEmail)}
          loading={isLoading || isSaving}
          disabled={!settings?.emailVerified}
          onClick={() => onToggle('discoverableByEmail')}
        />
        <PreferenceToggle
          label={settings?.phoneVerified ? 'Find me by verified phone' : 'Verify phone to enable discovery'}
          checked={Boolean(settings?.discoverableByPhone)}
          loading={isLoading || isSaving}
          disabled={!settings?.phoneVerified}
          onClick={() => onToggle('discoverableByPhone')}
        />
      </CardContent>
    </Card>
  );
}

function ContactStatusRow({
  icon: Icon,
  label,
  value,
  verified,
  missing,
  actionLabel,
  actionLoading,
  onAction,
}: {
  icon: ElementType;
  label: string;
  value: string;
  verified: boolean;
  missing?: boolean;
  actionLabel?: string;
  actionLoading?: boolean;
  onAction?: () => void;
}) {
  const ready = !missing && verified;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text">{label}</p>
          <p className="truncate text-sm text-text-muted">{value}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
            ready
              ? 'border-primary/20 bg-primary/10 text-primary'
              : 'border-amber-300 bg-amber-50 text-amber-800'
          )}
        >
          {ready ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
          {ready ? 'Verified' : missing ? 'Missing' : 'Verify'}
        </span>
        {actionLabel && onAction ? (
          <Button variant="ghost" size="xs" isLoading={Boolean(actionLoading)} onClick={onAction}>
            {actionLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function WorkspaceCard({
  title,
  subtitle,
  active,
  loading,
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string;
  active: boolean;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/30 p-4">
      <div>
        <p className="font-semibold text-text">{title}</p>
        <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
      </div>
      <Button
        variant={active ? 'primary' : 'outline'}
        size="sm"
        disabled={disabled}
        isLoading={loading}
        onClick={onClick}
      >
        {active ? 'Active' : 'Open'}
      </Button>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: ElementType;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-2xl border border-border bg-surface-elevated/30 p-4 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
    >
      <div className="rounded-2xl bg-primary/10 p-3 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="font-semibold text-text">{title}</p>
        <p className="mt-1 text-sm text-text-muted">{description}</p>
      </div>
    </button>
  );
}

function PreferenceToggle({
  label,
  checked,
  loading,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  loading: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={loading || disabled}
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="text-sm font-semibold text-text">{label}</span>
      <span
        className={cn(
          'inline-flex h-6 w-11 items-center rounded-full border px-1 transition-colors',
          checked ? 'border-primary bg-primary' : 'border-border bg-surface-elevated'
        )}
      >
        <span
          className={cn(
            'h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </span>
    </button>
  );
}

function ActionRow({
  icon: Icon,
  label,
  actionLabel,
  danger,
  disabled,
  onClick,
}: {
  icon: ElementType;
  label: string;
  actionLabel?: string;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3 text-left transition-colors',
        !disabled && 'hover:border-primary/30 hover:bg-surface-elevated',
        disabled && 'cursor-not-allowed opacity-60'
      )}
    >
      <span className="flex items-center gap-3">
        <span className={cn('rounded-xl p-2', danger ? 'bg-red-500/10 text-red-500' : 'bg-primary/10 text-primary')}>
          <Icon className="h-4 w-4" />
        </span>
        <span className={cn('text-sm font-semibold', danger ? 'text-red-500' : 'text-text')}>{label}</span>
      </span>
      <span className="text-xs font-semibold text-text-subtle">{actionLabel || <ArrowRight className="h-4 w-4" />}</span>
    </button>
  );
}

function ProfileFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
      <p className="text-xs uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-2 text-sm font-semibold text-text">{value}</p>
    </div>
  );
}

function MiniStatus({ label, ready }: { label: string; ready: boolean }) {
  return (
    <div
      className={cn(
        'rounded-2xl border px-4 py-3',
        ready ? 'border-primary/20 bg-primary/10 text-primary' : 'border-amber-300 bg-amber-50 text-amber-800'
      )}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.16em]">{label}</p>
      <p className="mt-1 text-sm font-semibold">{ready ? 'Verified' : 'Needs verify'}</p>
    </div>
  );
}

function InlineNotice({
  tone,
  title,
  message,
}: {
  tone: 'success' | 'error';
  title: string;
  message?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-3xl border p-4 text-sm shadow-[var(--shadow-card)]',
        tone === 'success'
          ? 'border-primary/20 bg-primary/10 text-primary'
          : 'border-red-200 bg-red-50 text-red-700'
      )}
    >
      <p className="font-semibold">{title}</p>
      {message ? <p className="mt-1">{message}</p> : null}
    </div>
  );
}

function buildProfileFacts(profile?: WebUserProfile): Array<{ label: string; value: string }> {
  const ranks = [profile?.ustaRank && `NTRP ${profile.ustaRank}`, profile?.utrRank && `UTR ${profile.utrRank}`]
    .filter(Boolean)
    .join(' / ');
  const homeCourts = profile?.homeCourts?.map((court) => court.name).filter(Boolean).join(', ');

  return [
    { label: 'Profile name', value: profile?.profileName || 'Not set' },
    { label: 'ZIP', value: profile?.zipCode || 'Not set' },
    { label: 'Sport', value: formatSport(profile?.sport) },
    { label: 'Play style', value: formatChoice(profile?.playStyle) },
    { label: 'Hand', value: formatChoice(profile?.handedness) },
    { label: 'Ratings', value: ranks ? `${ranks} · Self-reported` : profile?.pickleballRank || 'Not set' },
    { label: 'Home courts', value: homeCourts || 'Not set' },
    { label: 'Age range', value: profile?.ageRange || 'Not set' },
  ];
}

function getDisplayName(profile?: WebUserProfile, fallback?: string | null): string {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return fullName || profile?.profileName || fallback || 'Set your name';
}

function normalizeVisibility(value?: string | null): VisibilityMode {
  return value === 'private' || value === 'public' || value === 'squads' ? value : 'squads';
}

function formatSport(value?: string | null): string {
  if (value === 'both') return 'Tennis + Pickleball';
  return formatChoice(value);
}

function formatChoice(value?: string | null): string {
  if (!value) return 'Not set';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function resolveProfileErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: unknown; error?: unknown } } }).response;
    if (typeof response?.data?.message === 'string') {
      return response.data.message;
    }
    if (typeof response?.data?.error === 'string') {
      return response.data.error;
    }
  }

  return getErrorMessage(error);
}

function dataExportActionLabel(
  status: 'NONE' | 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' | 'EXPIRED' | undefined,
  isLoading: boolean,
  isError: boolean,
  isActing: boolean
): string {
  if (isActing) return 'Please wait';
  if (isLoading) return 'Checking';
  if (isError && !status) return 'Retry';
  if (status === 'PENDING' || status === 'PROCESSING') return 'Preparing';
  if (status === 'READY') return 'Download';
  if (status === 'EXPIRED') return 'Prepare again';
  if (status === 'FAILED') return 'Try again';
  return 'Prepare';
}
